import React, { useState } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
const CallActivityChart = ({ data }) => {
  const [chartType, setChartType] = useState('area');

  const metricConfig = {
    total: { color: 'hsl(var(--primary))', label: 'Total' },
    success: { color: 'hsl(142 76% 36%)', label: 'Réussis' },
    failed: { color: 'hsl(var(--destructive))', label: 'Échoués' },
    callback: { color: 'hsl(38 92% 50%)', label: 'Rappels' },
  };

  const [selectedMetrics, setSelectedMetrics] = useState(['total', 'success', 'callback']);

  const toggleMetric = (metric) => {
    setSelectedMetrics((prev) =>
      prev.includes(metric) ? prev.filter((m) => m !== metric) : [...prev, metric],
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-lg">
        <p className="mb-2 text-xs font-medium text-popover-foreground">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 text-xs">
            <span className="flex items-center gap-2 text-muted-foreground">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}
            </span>
            <span className="font-semibold tabular-nums text-foreground">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const ChartComponent = chartType === 'line' ? LineChart : AreaChart;
  const DataComponent = chartType === 'line' ? Line : Area;

  return (
    <div className="rounded-2xl border border-border/80 bg-card p-5 shadow-sm md:p-6">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground md:text-lg">Activité quotidienne</h3>
          <p className="text-sm text-muted-foreground">Volume d&apos;appels sur la période</p>
        </div>
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5">
          <button
            type="button"
            onClick={() => setChartType('area')}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              chartType === 'area'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Zone
          </button>
          <button
            type="button"
            onClick={() => setChartType('line')}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              chartType === 'line'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Ligne
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {Object.entries(metricConfig).map(([key, config]) => {
          const active = selectedMetrics.includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggleMetric(key)}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                active
                  ? 'bg-primary/10 text-primary ring-1 ring-primary/25'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: config.color, opacity: active ? 1 : 0.35 }}
              />
              {config.label}
            </button>
          );
        })}
      </div>

      <div className="h-64 w-full md:h-72" aria-label="Graphique d'activité d'appels">
        {data?.length === 0 ? (
          <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-border bg-muted/10 text-sm text-muted-foreground">
            Aucune donnée à afficher
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ChartComponent data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              {selectedMetrics.map((metric) => (
                <DataComponent
                  key={metric}
                  type="monotone"
                  dataKey={metric}
                  name={metricConfig[metric]?.label}
                  stroke={metricConfig[metric]?.color}
                  fill={chartType === 'area' ? metricConfig[metric]?.color : undefined}
                  fillOpacity={chartType === 'area' ? 0.12 : undefined}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </ChartComponent>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default CallActivityChart;
